/**
 * ReactionsBar — Fire/Drool/ChefKiss/Yum reactions on posts
 * Compact horizontal bar that replaces simple like button
 */
import { useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const REACTIONS = [
  { type: 'fire', emoji: '\u{1F525}', label: 'Fire' },
  { type: 'drool', emoji: '\u{1F924}', label: 'Drool' },
  { type: 'chef_kiss', emoji: '\u{1F60B}', label: 'Chef' },
  { type: 'yum', emoji: '\u{1F60B}', label: 'Yum' },
  { type: 'heart', emoji: '\u{2764}\u{FE0F}', label: 'Love' },
  { type: '100', emoji: '\u{1F4AF}', label: '100' },
];

const ReactionsBar = ({ postId, initialCounts = {}, initialViewerReactions = [] }) => {
  const { user } = useAuth();
  const [counts, setCounts] = useState(initialCounts);
  const [viewerReactions, setViewerReactions] = useState(new Set(initialViewerReactions));
  const [showPicker, setShowPicker] = useState(false);

  const toggleReaction = useCallback(async (type) => {
    if (!user) return;
    const token = localStorage.getItem('fq_auth_token');
    try {
      const res = await fetch(`${API_URL}/api/reactions/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) },
        credentials: 'include',
        body: JSON.stringify({ post_id: postId, reaction_type: type }),
      });
      if (res.ok) {
        const data = await res.json();
        setCounts(data.counts || {});
        setViewerReactions(prev => {
          const next = new Set(prev);
          if (data.action === 'added') next.add(type);
          else next.delete(type);
          return next;
        });
      }
    } catch {}
    setShowPicker(false);
  }, [user, postId]);

  const total = counts.total || 0;
  const topReactions = REACTIONS.filter(r => (counts[r.type] || 0) > 0).slice(0, 3);

  return (
    <div className="relative inline-flex items-center">
      {/* Quick reaction summary */}
      <button onClick={() => setShowPicker(!showPicker)}
        className="flex items-center gap-1 px-2 py-1 rounded-full hover:bg-gray-50 transition-colors text-gray-500"
        data-testid={`reactions-trigger-${postId}`}>
        {topReactions.length > 0 ? (
          <>
            <span className="flex -space-x-0.5">
              {topReactions.map(r => <span key={r.type} className="text-sm">{r.emoji}</span>)}
            </span>
            {total > 0 && <span className="text-xs font-medium text-gray-500 ml-0.5">{total}</span>}
          </>
        ) : (
          <span className="text-sm">+</span>
        )}
      </button>

      {/* Reaction picker popup */}
      {showPicker && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowPicker(false)} />
          <div className="absolute bottom-full left-0 mb-1 bg-white rounded-2xl shadow-xl border border-gray-100 p-1.5 flex gap-0.5 z-50" data-testid={`reactions-picker-${postId}`}>
            {REACTIONS.map(r => (
              <button key={r.type} onClick={() => toggleReaction(r.type)}
                className={`w-9 h-9 flex items-center justify-center rounded-full transition-all hover:scale-125 hover:bg-gray-50 ${viewerReactions.has(r.type) ? 'bg-[#FFF5F0] scale-110' : ''}`}
                title={r.label}>
                <span className="text-lg">{r.emoji}</span>
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default ReactionsBar;
